// Impresión de una etiqueta (fase 1: diálogo de impresión del sistema).
// - iOS: Safari no imprime bien un iframe oculto, así que se abre el PDF de la
//   etiqueta en una pestaña. La pestaña se abre en el MISMO clic con
//   prepararImpresion() (si se abre después de esperar al servidor, Safari la
//   bloquea como ventana emergente) y luego se le pone la dirección del PDF.
// - Resto: se pide el HTML de la etiqueta, se carga en un iframe oculto de
//   62 mm y se lanza print(). Si algo falla, se abre el PDF en una pestaña.
import { etiquetasAPI, etiquetaPdfUrl } from '../api/api';

const MS_QUITAR_IFRAME = 60000;

export function esIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1;
}

// Llamar en el clic, antes de cualquier await. En iOS devuelve la pestaña
// abierta (o null si el navegador no la deja abrir); fuera de iOS, null.
export function prepararImpresion() {
  if (!esIOS()) return null;
  try {
    const ventana = window.open('', '_blank');
    if (ventana && ventana.document) {
      try {
        ventana.document.title = 'Etiqueta';
        ventana.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 24px; color: #374151;">Preparando la etiqueta...</p>';
      } catch (e) {
        // La pestaña vacía puede no dejar escribir: no pasa nada
      }
    }
    return ventana || null;
  } catch (e) {
    return null;
  }
}

// Cierra la pestaña preparada si al final no hay nada que imprimir (error)
export function cancelarImpresion(ventana) {
  try {
    if (ventana && !ventana.closed) ventana.close();
  } catch (e) {
    // nada
  }
}

const abrirPdf = (id) => {
  try {
    window.open(etiquetaPdfUrl(id), '_blank');
  } catch (e) {
    // sin ventanas emergentes no hay más que hacer
  }
};

const imprimirEnIframe = (html) => new Promise((resolve, reject) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.title = 'Etiqueta para imprimir';
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '62mm',
    height: '50mm',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  });
  let lanzado = false;
  const quitar = () => {
    try {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    } catch (e) {
      // nada
    }
  };
  iframe.onload = () => {
    if (lanzado) return;
    lanzado = true;
    try {
      const w = iframe.contentWindow;
      w.focus();
      w.print();
      setTimeout(quitar, MS_QUITAR_IFRAME);
      resolve();
    } catch (e) {
      quitar();
      reject(e);
    }
  };
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
});

// Imprime la etiqueta de una apertura. `ventana` es lo que devolvió
// prepararImpresion() en el mismo clic. Devuelve una promesa.
export async function imprimirEtiqueta(apertura, ventana = null) {
  const id = apertura?.id;
  if (!id) throw new Error('Etiqueta sin identificador');
  if (esIOS()) {
    const url = etiquetaPdfUrl(id);
    if (ventana && !ventana.closed) {
      ventana.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    return;
  }
  try {
    const { data } = await etiquetasAPI.etiquetaHtml(id);
    const html = typeof data === 'string' ? data : String(data || '');
    if (!html) throw new Error('Etiqueta vacía');
    await imprimirEnIframe(html);
  } catch (e) {
    abrirPdf(id);
  }
}

export default imprimirEtiqueta;
